import { SaveManager } from '../../../../../state/SaveManager.js';
import type { WordGardenConfig, WordGardenHint } from './WordGardenConfig.js';
import {
  WordGardenState,
  type WordGardenSnapshot,
  type WordGardenSubmissionResult,
} from './WordGardenState.js';

export interface WordGardenChallengeEvents {
  readonly onStateChanged?: (snapshot: WordGardenSnapshot) => void;
  readonly onIncorrect?: (snapshot: WordGardenSnapshot) => void;
  readonly onCompleted?: (config: WordGardenConfig, snapshot: WordGardenSnapshot) => void;
}

/**
 * UI-independent vocabulary mechanic. Production garden art and input can bind
 * to these commands without embedding curriculum content in a Phaser scene.
 */
export class WordGardenChallenge {
  readonly state: WordGardenState;
  private completionEmitted = false;

  constructor(
    readonly config: WordGardenConfig,
    private readonly events: WordGardenChallengeEvents = {},
  ) {
    this.state = new WordGardenState(config);
  }

  getSnapshot() { return this.state.snapshot(); }

  selectChoice(choiceId: string) {
    const changed = this.state.selectChoice(choiceId);
    if (changed) this.emitState();
    return changed;
  }

  requestHint(): WordGardenHint {
    const hint = this.state.requestHint();
    this.emitState();
    return hint;
  }

  submit(): WordGardenSubmissionResult {
    const before = this.state.snapshot();
    if (before.completed) return 'correct';

    const result = this.state.submit();
    const snapshot = this.state.snapshot();
    this.events.onStateChanged?.(snapshot);
    if (result === 'incomplete') return result;

    SaveManager.recordAnswer(this.config.skillId, result === 'correct');
    if (result === 'incorrect') {
      this.events.onIncorrect?.(snapshot);
      return result;
    }
    if (!this.completionEmitted) {
      this.completionEmitted = true;
      this.events.onCompleted?.(this.config, snapshot);
    }
    return result;
  }

  reset() {
    const changed = this.state.reset();
    if (changed) this.emitState();
    return changed;
  }

  private emitState() {
    this.events.onStateChanged?.(this.state.snapshot());
  }
}
